import { Link, useNavigate } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { Search, ShoppingCart, User, Menu, X, Heart, LogIn, ChevronDown, ChevronRight } from "lucide-react";
import logoAsset from "@/assets/rc-chemical-lab-logo.png.asset.json";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useState, useRef, useEffect, useCallback } from "react";
import type { Product } from "@/data/types";

interface SubLink {
  to: string;
  label: string;
}

interface NavItem {
  to: string;
  label: string;
  subLinks?: SubLink[];
}

const Header = () => {
  const { totalItems } = useCart();
  const { user, loading } = useAuth();
  const { items: wishlistItems } = useWishlist();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const productsRef = useRef<Product[] | null>(null);

  // Lazy load products only when user starts searching
  const loadAndSearch = useCallback(async (query: string) => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    if (!productsRef.current) {
      const mod = await import("@/data/products");
      productsRef.current = mod.products;
    }
    const results = productsRef.current
      .filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        (p.cas && p.cas.toLowerCase().includes(q))
      )
      .slice(0, 8);
    setSuggestions(results);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadAndSearch(searchQuery);
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, loadAndSearch]);

  // Reset active index when suggestions change
  useEffect(() => { setActiveIndex(-1); }, [suggestions]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    const total = suggestions.length + 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % total);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => (i - 1 + total) % total);
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      if (activeIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeIndex].slug);
      } else {
        handleSearch(e as unknown as React.FormEvent);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  }, [showSuggestions, suggestions, activeIndex]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target as Node) &&
        mobileSearchRef.current && !mobileSearchRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/shop?search=${encodeURIComponent(q)}`);
      setSearchQuery("");
      setShowSuggestions(false);
      setMobileOpen(false);
    }
  };

  const handleSuggestionClick = (slug: string) => {
    navigate(`/product/${slug}`);
    setSearchQuery("");
    setShowSuggestions(false);
    setMobileOpen(false);
  };

  const navLinks: NavItem[] = [
    { to: "/shop", label: "Shop All" },
    {
      to: "/research-chemicals",
      label: "Research Chemicals",
      subLinks: [
        { to: "/research-chemicals?sub=Tryptamines", label: "Tryptamines" },
        { to: "/research-chemicals?sub=Lysergamides", label: "Lysergamides" },
        { to: "/research-chemicals?sub=Phenethylamines", label: "Phenethylamines" },
        { to: "/research-chemicals?sub=Dissociatives", label: "Dissociatives" },
        { to: "/research-chemicals?sub=Cathinones", label: "Cathinones" },
        { to: "/research-chemicals?sub=Cannabinoids", label: "Cannabinoids" },
        { to: "/research-chemicals?sub=Benzodiazepines", label: "Benzodiazepines" },
        { to: "/research-chemicals?sub=Opioids", label: "Opioids" },
        { to: "/research-chemicals?sub=Amphetamines", label: "Amphetamines" },
        { to: "/research-chemicals?sub=Nootropics", label: "Nootropics" },
        { to: "/research-chemicals?sub=Phenidates", label: "Phenidates" },
      ],
    },
    {
      to: "/industrial-chemicals",
      label: "Industrial Chemicals",
      subLinks: [
        { to: "/industrial-chemicals?sub=Acids", label: "Acids" },
        { to: "/industrial-chemicals?sub=Bases+%2F+Alkalis", label: "Bases / Alkalis" },
        { to: "/industrial-chemicals?sub=Solvents+%26+Alcohols", label: "Solvents & Alcohols" },
        { to: "/industrial-chemicals?sub=Salts", label: "Salts" },
        { to: "/industrial-chemicals?sub=Oxidizers", label: "Oxidizers" },
        { to: "/industrial-chemicals?sub=Surfactants", label: "Surfactants" },
        { to: "/industrial-chemicals?sub=Chelating+Agents", label: "Chelating Agents" },
        { to: "/industrial-chemicals?sub=Metal+Compounds", label: "Metal Compounds" },
      ],
    },
    {
      to: "/liquid-mercury",
      label: "Liquid Mercury",
      subLinks: [
        { to: "/liquid-mercury?sub=Elemental+Mercury", label: "Elemental Mercury" },
        { to: "/liquid-mercury?sub=Red+Mercury", label: "Red Mercury" },
        { to: "/liquid-mercury?sub=Silver+Mercury", label: "Silver Mercury" },
      ],
    },
    {
      to: "/pills-painkillers",
      label: "Pills & Painkillers",
      subLinks: [
        { to: "/pills-painkillers?sub=Opioids", label: "Opioids" },
        { to: "/pills-painkillers?sub=Benzodiazepines", label: "Benzodiazepines" },
        { to: "/pills-painkillers?sub=Stimulants", label: "Stimulants" },
        { to: "/pills-painkillers?sub=Analgesics", label: "Analgesics" },
        { to: "/pills-painkillers?sub=Sedatives", label: "Sedatives" },
      ],
    },
    {
      to: "/cannabis",
      label: "Cannabis",
      subLinks: [
        { to: "/cannabis?sub=Isolates", label: "Isolates" },
        { to: "/cannabis?sub=Extracts", label: "Extracts" },
        { to: "/cannabis?sub=Standards", label: "Standards" },
      ],
    },
    { to: "/nootropics", label: "Nootropics" },
    {
      to: "/peptides",
      label: "Peptides",
      subLinks: [
        { to: "/peptides?sub=Native", label: "Native" },
        { to: "/peptides?sub=Mutant", label: "Mutant" },
        { to: "/peptides?sub=Fragments", label: "Fragments" },
        { to: "/peptides?sub=Labeled", label: "Labeled" },
        { to: "/peptides?sub=Synthetic", label: "Synthetic" },
        { to: "/peptides?sub=APP-Related", label: "APP-Related" },
      ],
    },
    {
      to: "/lab-supplies",
      label: "Lab Supplies",
      subLinks: [
        { to: "/moisture-analyzers", label: "Moisture Analyzers" },
        { to: "/fat-analyzers", label: "Fat Analyzers" },
        { to: "/balances-scales", label: "Balances & Scales" },
        { to: "/filters", label: "Filters" },
        { to: "/lab-supplies?sub=Lab+Supplies", label: "Lab Supplies" },
        { to: "/lab-supplies?sub=Lab+Equipment", label: "Lab Equipment" },
        { to: "/lab-supplies?sub=Service", label: "Service" },
      ],
    },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b">
      {/* Top bar */}
      <div className="gradient-navy text-primary-foreground text-xs py-1.5">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <span>🔬 For Laboratory & Research Use Only — Not for Human Consumption</span>
          <div className="hidden md:flex gap-4">
            <span>Text: +1 (458) 888-9647</span>
            <span>Telegram: @chemwayusa</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" aria-label="RC Chemical Lab home">
          <img src={logoAsset.url} alt="RC Chemical Lab" className="h-20 md:h-28 w-auto max-w-[280px] md:max-w-[420px] object-contain drop-shadow-sm" />
        </Link>

        {/* Search */}
        <div ref={searchRef} className="hidden md:flex flex-1 max-w-xl relative">
          <form onSubmit={handleSearch} className="w-full relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search products, CAS numbers, categories..."
              aria-label="Search products, CAS numbers, and categories"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              role="combobox"
              aria-expanded={showSuggestions && suggestions.length > 0}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined}
            />
          </form>
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto" role="listbox">
              {suggestions.map((p, i) => (
                <button
                  key={p.id}
                  id={`suggestion-${i}`}
                  role="option"
                  aria-selected={activeIndex === i}
                  onClick={() => handleSuggestionClick(p.slug)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted transition-colors ${activeIndex === i ? 'bg-muted' : ''}`}
                >
                  {p.image && <img src={p.image} alt="" className="w-8 h-8 rounded object-cover shrink-0" loading="lazy" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.category}{p.cas ? ` · ${p.cas}` : ''}</div>
                  </div>
                  <span className="ml-auto text-sm font-semibold text-primary shrink-0">${p.price.toFixed(2)}</span>
                </button>
              ))}
              <button
                id={`suggestion-${suggestions.length}`}
                role="option"
                aria-selected={activeIndex === suggestions.length}
                onClick={() => { handleSearch({ preventDefault: () => {} } as React.FormEvent); }}
                className={`w-full px-4 py-2 text-sm text-primary hover:bg-muted border-t text-center ${activeIndex === suggestions.length ? 'bg-muted' : ''}`}
              >
                View all results for "{searchQuery}"
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {!loading && (
            <>
              {user ? (
                <>
                  <Link to="/account?tab=wishlist" aria-label={`Wishlist${wishlistItems.length > 0 ? ` (${wishlistItems.length} items)` : ""}`}>
                    <Button variant="ghost" size="icon" className="relative hidden sm:inline-flex" aria-label="Open wishlist">
                      <Heart className="h-5 w-5" />
                      {wishlistItems.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {wishlistItems.length}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Link to="/account" aria-label="My account">
                    <Button variant="ghost" size="icon" className="hidden md:inline-flex" aria-label="My account">
                      <User className="h-5 w-5" />
                    </Button>
                  </Link>
                </>
              ) : (
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="hidden md:inline-flex gap-2">
                    <LogIn className="h-4 w-4" /> Sign In
                  </Button>
                </Link>
              )}
            </>
          )}
          <Link to="/cart">
            <Button variant="ghost" size="sm" className="relative gap-2">
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline">Cart</span>
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Button>
          </Link>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} aria-controls="mobile-nav">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Desktop nav */}
      <nav className="hidden md:block border-t bg-surface">
        <div className="container mx-auto px-4 flex gap-1">
          {navLinks.map((link) => (
            <div key={link.label} className="relative group">
              <NavLink
                to={link.to}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors rounded-t-md inline-flex items-center gap-1"
                activeClassName="text-primary border-b-2 border-primary bg-muted/50"
              >
                {link.label}
                {link.subLinks && <ChevronDown className="h-3 w-3" />}
              </NavLink>
              {link.subLinks && (
                <div className="absolute left-0 top-full z-50 hidden group-hover:block pt-1">
                  <div className="bg-popover border rounded-md shadow-lg py-1 min-w-[200px]">
                    {link.subLinks.map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.to}
                        className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="md:hidden border-t bg-background p-4 space-y-1 max-h-[70vh] overflow-y-auto">
          {/* Mobile search */}
          <div ref={mobileSearchRef} className="relative mb-3">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search products..."
                aria-label="Search products"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </form>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
                {suggestions.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleSuggestionClick(p.slug)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.category}</div>
                    </div>
                    <span className="ml-auto text-sm font-semibold text-primary shrink-0">${p.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {navLinks.map((link) => (
            <div key={link.label}>
              <div className="flex items-center">
                <NavLink
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                  activeClassName="text-primary bg-primary/10"
                >
                  {link.label}
                </NavLink>
                {link.subLinks && (
                  <button
                    onClick={() => setExpandedMobile(expandedMobile === link.label ? null : link.label)}
                    className="p-2 text-muted-foreground hover:text-foreground"
                  >
                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedMobile === link.label ? 'rotate-90' : ''}`} />
                  </button>
                )}
              </div>
              {link.subLinks && expandedMobile === link.label && (
                <div className="ml-4 pl-2 border-l border-border space-y-0.5">
                  {link.subLinks.map((sub) => (
                    <Link
                      key={sub.label}
                      to={sub.to}
                      onClick={() => setMobileOpen(false)}
                      className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="border-t pt-2 mt-2">
            {user ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                >
                  <User className="h-4 w-4 inline mr-2" /> My Account
                </Link>
                <Link
                  to="/account?tab=wishlist"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                >
                  <Heart className="h-4 w-4 inline mr-2" /> Wishlist
                  {wishlistItems.length > 0 && (
                    <span className="ml-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1.5 py-0.5">
                      {wishlistItems.length}
                    </span>
                  )}
                </Link>
              </>
            ) : (
              <Link
                to="/auth"
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
              >
                <LogIn className="h-4 w-4 inline mr-2" /> Sign In
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
